import {
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    makeStyles,
    Typography,
    LinearProgress,
    Tooltip,
} from '@material-ui/core';
import { Content, Header, Page, Link } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { Entity } from '@backstage/catalog-model';

const LIFECYCLE_PHASES = ['poc', 'dev', 'production', 'end-of-life'] as const;
type Phase = (typeof LIFECYCLE_PHASES)[number];

const PHASE_CONFIG: Record<
    Phase,
    { label: string; color: string; description: string }
> = {
    poc: {
        label: 'PoC',
        color: '#FF9800',
        description: 'Proof of Concept – lokal, keine Cloud-Daten',
    },
    dev: {
        label: 'Dev',
        color: '#2196F3',
        description: 'In Azure Dev deployed – keine kritischen Daten',
    },
    production: {
        label: 'Prod',
        color: '#4CAF50',
        description: 'Produktiv – Monitoring, Updates, Kostenkontrolle',
    },
    'end-of-life': {
        label: 'End-of-Life',
        color: '#9E9E9E',
        description: 'Ausser Betrieb – archiviert / gelöscht',
    },
};

const EXIT_CRITERIA: Record<Phase, string[]> = {
    poc: [
        'Ziel/Scope dokumentiert',
        'Minimaler Funktionsnachweis',
        'DSFA (falls relevant)',
        'Architektur-Zeichnung',
        'Portmatrix',
        'Go/No-Go Entscheid',
    ],
    dev: [
        'Health Endpoint vorhanden',
        'Logs/Metriken via App Insights',
        'Azure Budget aktiviert',
        'Monitoring/Alerting nachgewiesen',
        'Dependencies aktuell',
        'DSFA abgeschlossen',
        'Benefit/Use Case validiert',
        'Go/No-Go Entscheid',
    ],
    production: [
        'Betriebsverantwortung definiert und Übergabe an APM',
        'Monitoring/Alerting aktiv',
        'Dependency-Updates regelmässig',
        'Kostenkontrolle aktiv',
        'Usage-/PowerBI-Report',
        'Quartals-Review durchgeführt',
    ],
    'end-of-life': [
        'Daten archiviert/gelöscht',
        'Azure Ressourcen entfernt',
        'Secrets/Zugriffe gelöscht',
        'Git Repo archiviert',
    ],
};

interface CriterionCheck {
    label: string;
    annotationKey?: string;
    requiredValue?: string;
}

const PHASE_CRITERIA: Record<Phase, CriterionCheck[]> = {
    poc: [
        { label: 'Ziel/Scope dokumentiert', annotationKey: 'itkom.pbs.ch/scope-documented', requiredValue: 'true' },
        { label: 'Minimaler Funktionsnachweis', annotationKey: 'itkom.pbs.ch/poc-validated', requiredValue: 'true' },
        { label: 'DSFA (falls relevant)', annotationKey: 'itkom.pbs.ch/dsfa-status', requiredValue: 'completed' },
        { label: 'Architektur-Zeichnung', annotationKey: 'itkom.pbs.ch/architecture-diagram', requiredValue: 'completed' },
        { label: 'Portmatrix', annotationKey: 'itkom.pbs.ch/portmatrix', requiredValue: 'completed' },
        { label: 'Go/No-Go Entscheid', annotationKey: 'itkom.pbs.ch/go-nogo', requiredValue: 'approved' },
    ],
    dev: [
        { label: 'Health Endpoint vorhanden', annotationKey: 'itkom.pbs.ch/health-endpoint', requiredValue: 'true' },
        { label: 'Logs/Metriken via App Insights', annotationKey: 'itkom.pbs.ch/monitoring-status', requiredValue: 'basic' },
        { label: 'Azure Budget aktiviert', annotationKey: 'itkom.pbs.ch/cost-budget', requiredValue: 'active' },
        { label: 'Monitoring/Alerting nachgewiesen', annotationKey: 'itkom.pbs.ch/alerting-status', requiredValue: 'active' },
        { label: 'Dependencies aktuell', annotationKey: 'itkom.pbs.ch/dependencies-current', requiredValue: 'true' },
        { label: 'DSFA abgeschlossen', annotationKey: 'itkom.pbs.ch/dsfa-status', requiredValue: 'completed' },
        { label: 'Benefit/Use Case validiert', annotationKey: 'itkom.pbs.ch/usecase-validated', requiredValue: 'true' },
        { label: 'Go/No-Go Entscheid', annotationKey: 'itkom.pbs.ch/go-nogo', requiredValue: 'approved' },
    ],
    production: [
        { label: 'Betriebsverantwortung definiert und Übergabe an APM', annotationKey: 'itkom.pbs.ch/ops-owner', requiredValue: 'assigned' },
        { label: 'Monitoring/Alerting aktiv', annotationKey: 'itkom.pbs.ch/monitoring-status', requiredValue: 'active' },
        { label: 'Dependency-Updates regelmässig', annotationKey: 'itkom.pbs.ch/dependencies-current', requiredValue: 'true' },
        { label: 'Kostenkontrolle aktiv', annotationKey: 'itkom.pbs.ch/cost-budget', requiredValue: 'active' },
        { label: 'Usage-/PowerBI-Report', annotationKey: 'itkom.pbs.ch/usage-report', requiredValue: 'active' },
        { label: 'Quartals-Review durchgeführt', annotationKey: 'itkom.pbs.ch/quarterly-review', requiredValue: 'true' },
    ],
    'end-of-life': [
        { label: 'Daten archiviert/gelöscht', annotationKey: 'itkom.pbs.ch/data-archived', requiredValue: 'true' },
        { label: 'Azure Ressourcen entfernt', annotationKey: 'itkom.pbs.ch/azure-cleanup', requiredValue: 'true' },
        { label: 'Secrets/Zugriffe gelöscht', annotationKey: 'itkom.pbs.ch/secrets-removed', requiredValue: 'true' },
        { label: 'Git Repo archiviert', annotationKey: 'itkom.pbs.ch/repo-archived', requiredValue: 'true' },
    ],
};

function evaluateCriterion(
    entity: Entity,
    criterion: CriterionCheck,
): 'met' | 'pending' | 'missing' {
    if (!criterion.annotationKey) return 'missing';
    const value = entity.metadata.annotations?.[criterion.annotationKey];
    if (!value) return 'missing';
    if (value === criterion.requiredValue) return 'met';
    return 'pending';
}

const useStyles = makeStyles(theme => ({
    phaseColumn: {
        minHeight: 400,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
    },
    phaseHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
    },
    phaseBadge: {
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.8rem',
    },
    entityCard: {
        marginBottom: theme.spacing(1.5),
        cursor: 'pointer',
        '&:hover': {
            boxShadow: theme.shadows[4],
        },
    },
    entityName: {
        fontWeight: 600,
        fontSize: '0.95rem',
    },
    entityMeta: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    statusChip: {
        fontSize: '0.65rem',
        height: 20,
        marginRight: theme.spacing(0.5),
        marginTop: theme.spacing(0.5),
    },
    summaryBar: {
        display: 'flex',
        gap: theme.spacing(3),
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        flexWrap: 'wrap',
    },
    summaryItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 80,
    },
    summaryValue: {
        fontSize: '2rem',
        fontWeight: 700,
    },
    summaryLabel: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
    },
    previousUnmetList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
    previousUnmetItem: {
        fontSize: '0.65rem',
        color: '#f44336',
        padding: '1px 0',
    },
    criteriaList: {
        listStyle: 'none',
        padding: 0,
        margin: theme.spacing(1, 0, 0, 0),
        '& li': {
            fontSize: '0.7rem',
            color: theme.palette.text.secondary,
            padding: '1px 0',
            '&::before': {
                content: '"☐ "',
            },
        },
    },
}));

function getAnnotation(entity: Entity, key: string): string | undefined {
    return entity.metadata.annotations?.[key];
}

function getLifecycle(entity: Entity): Phase {
    const annotationPhase = getAnnotation(entity, 'itkom.pbs.ch/lifecycle-phase');
    const raw = annotationPhase ?? (entity.spec as { lifecycle?: string })?.lifecycle ?? 'unknown';
    const normalized = raw.toLowerCase().replace(/[_\s]/g, '-');
    if (LIFECYCLE_PHASES.includes(normalized as Phase)) {
        return normalized as Phase;
    }
    // Map common Backstage values
    if (normalized === 'experimental') return 'poc';
    if (normalized === 'development') return 'dev';
    return 'poc'; // fallback
}

function getStatusChips(entity: Entity): { label: string; color: string }[] {
    const chips: { label: string; color: string }[] = [];
    const dsfa = getAnnotation(entity, 'itkom.pbs.ch/dsfa-status');
    const monitoring = getAnnotation(entity, 'itkom.pbs.ch/monitoring-status');
    const budget = getAnnotation(entity, 'itkom.pbs.ch/cost-budget');
    const arch = getAnnotation(entity, 'itkom.pbs.ch/architecture-diagram');

    if (dsfa === 'completed') chips.push({ label: 'DSFA ✓', color: '#4CAF50' });
    else if (dsfa === 'pending')
        chips.push({ label: 'DSFA ⏳', color: '#FF9800' });

    if (arch === 'completed')
        chips.push({ label: 'Architektur ✓', color: '#4CAF50' });
    else if (arch === 'pending')
        chips.push({ label: 'Architektur ⏳', color: '#FF9800' });

    if (monitoring === 'active')
        chips.push({ label: 'Monitoring ✓', color: '#4CAF50' });
    else if (monitoring === 'basic')
        chips.push({ label: 'Monitoring ~', color: '#FF9800' });

    if (budget === 'active')
        chips.push({ label: 'Budget ✓', color: '#4CAF50' });

    return chips;
}

export const LifecycleOverviewPage = () => {
    const classes = useStyles();
    const catalogApi = useApi(catalogApiRef);

    const { value: entities, loading } = useAsync(async () => {
        const response = await catalogApi.getEntities({
            filter: { kind: ['System'] },
            fields: [
                'metadata.name',
                'metadata.namespace',
                'metadata.annotations',
                'metadata.description',
                'metadata.title',
                'spec.lifecycle',
                'spec.type',
                'spec.owner',
                'kind',
            ],
        });
        return response.items;
    }, []);

    const grouped = (entities ?? []).reduce(
        (acc, entity) => {
            const phase = getLifecycle(entity);
            if (!acc[phase]) acc[phase] = [];
            acc[phase].push(entity);
            return acc;
        },
        {} as Record<Phase, Entity[]>,
    );

    const total = entities?.length ?? 0;

    return (
        <Page themeId="tool">
            <Header
                title="App Lifecycle Overview"
                subtitle="Übersicht aller Applikationen nach Lifecycle-Phase (PoC → Dev → Prod → End-of-Life)"
            />
            <Content>
                {loading && <LinearProgress />}

                {/* Summary bar */}
                <Box className={classes.summaryBar}>
                    <Box className={classes.summaryItem}>
                        <Typography
                            className={classes.summaryValue}
                            style={{ color: '#333' }}
                        >
                            {total}
                        </Typography>
                        <Typography className={classes.summaryLabel}>Total</Typography>
                    </Box>
                    {LIFECYCLE_PHASES.map(phase => (
                        <Box key={phase} className={classes.summaryItem}>
                            <Typography
                                className={classes.summaryValue}
                                style={{ color: PHASE_CONFIG[phase].color }}
                            >
                                {(grouped[phase] ?? []).length}
                            </Typography>
                            <Typography className={classes.summaryLabel}>
                                {PHASE_CONFIG[phase].label}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Kanban columns */}
                <Grid container spacing={2}>
                    {LIFECYCLE_PHASES.map(phase => (
                        <Grid item xs={12} md={3} key={phase}>
                            <Box className={classes.phaseColumn}>
                                <Box className={classes.phaseHeader}>
                                    <Typography variant="h6">
                                        {PHASE_CONFIG[phase].label}
                                    </Typography>
                                    <Chip
                                        label={`${(grouped[phase] ?? []).length}`}
                                        size="small"
                                        className={classes.phaseBadge}
                                        style={{ backgroundColor: PHASE_CONFIG[phase].color }}
                                    />
                                </Box>
                                <Tooltip title={PHASE_CONFIG[phase].description}>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        style={{ display: 'block', marginBottom: 12 }}
                                    >
                                        {PHASE_CONFIG[phase].description}
                                    </Typography>
                                </Tooltip>

                                {(grouped[phase] ?? []).map(entity => {
                                    const name =
                                        entity.metadata.title ?? entity.metadata.name;
                                    const kind = entity.kind.toLowerCase();
                                    const namespace =
                                        entity.metadata.namespace ?? 'default';
                                    const chips = getStatusChips(entity);
                                    const entityPhase = getLifecycle(entity);
                                    const entityPhaseIndex = LIFECYCLE_PHASES.indexOf(entityPhase);
                                    const previousUnmet = LIFECYCLE_PHASES.slice(0, entityPhaseIndex).flatMap(prevPhase =>
                                        PHASE_CRITERIA[prevPhase]
                                            .map(c => ({ ...c, phase: prevPhase, status: evaluateCriterion(entity, c) }))
                                            .filter(c => c.status !== 'met'),
                                    );
                                    return (
                                        <Card
                                            key={entity.metadata.name}
                                            className={classes.entityCard}
                                            variant="outlined"
                                        >
                                            <CardContent style={{ padding: 12 }}>
                                                <Link
                                                    to={`/catalog/${namespace}/${kind}/${entity.metadata.name}`}
                                                >
                                                    <Typography className={classes.entityName}>
                                                        {name}
                                                    </Typography>
                                                </Link>
                                                <Typography className={classes.entityMeta}>
                                                    {(entity.spec as { type?: string })?.type} ·{' '}
                                                    {(entity.spec as { owner?: string })?.owner}
                                                </Typography>
                                                {entity.metadata.description && (
                                                    <Typography
                                                        className={classes.entityMeta}
                                                        style={{ marginTop: 4 }}
                                                    >
                                                        {entity.metadata.description}
                                                    </Typography>
                                                )}
                                                <Box>
                                                    {chips.map(c => (
                                                        <Chip
                                                            key={c.label}
                                                            label={c.label}
                                                            size="small"
                                                            className={classes.statusChip}
                                                            style={{
                                                                backgroundColor: c.color,
                                                                color: '#fff',
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                                {previousUnmet.length > 0 && (
                                                    <Box mt={0.5}>
                                                        <Typography style={{ fontSize: '0.65rem', color: '#f44336', fontWeight: 600 }}>
                                                            ⚠ {previousUnmet.length} offene Vorphasen-Aufgabe{previousUnmet.length !== 1 ? 'n' : ''}:
                                                        </Typography>
                                                        <ul className={classes.previousUnmetList}>
                                                            {previousUnmet.map((c, idx) => (
                                                                <li key={idx} className={classes.previousUnmetItem}>
                                                                    [{PHASE_CONFIG[c.phase].label}] {c.label}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Card>
                                    );
                                })}

                                {/* Exit criteria reminder */}
                                <Box mt={2}>
                                    <Typography
                                        variant="caption"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {phase === 'end-of-life'
                                            ? 'Cleanup-Checkliste:'
                                            : phase === 'production'
                                                ? 'Wartungs-Checkliste:'
                                                : 'Exit-Kriterien:'}
                                    </Typography>
                                    <ul className={classes.criteriaList}>
                                        {EXIT_CRITERIA[phase].map(c => (
                                            <li key={c}>{c}</li>
                                        ))}
                                    </ul>
                                </Box>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Content>
        </Page>
    );
};
